import {
  beginMockCheckoutActivation,
  consumePostCheckoutFreePoll,
  getLastCheckoutPlan,
  getMockAuthState,
  getMockOnboarded,
  getMockProfileFields,
  getMockScenario,
  getPostCheckoutFreePollsRemaining,
  mockJobStageMs,
  mockLatency,
  resetMockCheckoutState,
  setLastCheckoutPlan,
  setMockAuthState,
  setMockOnboarded,
  setMockProfileFields,
} from './mockState';
import {
  AcquireError,
  InvalidEmailError,
  PortalError,
  TwitchSignInRequiredError,
  type AcquireSuccess,
  type CheckoutSuccess,
  type Entitlement,
  type HighlightJob,
  type HighlightJobStatus,
  type PortalSuccess,
  type Profile,
  type ProfileUpdate,
  type ReelSummary,
  type StudioCheckoutPlan,
  type Vod,
} from './types';
import { STUDIO_CHECKOUT_ROUTES, STUDIO_COPY, STUDIO_PORTAL_RETURN_ROUTE, STUDIO_PRICING } from '../config';
import { isReelNameValid, normalizeReelName } from '../utils/reelName';

/** Public sample MP4 for playable mock result. */
export const MOCK_SAMPLE_MP4 =
  'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4';

export function mockPublicShareUrl(jobId: string): string {
  const alphabet =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let n = 2166136261;
  for (let i = 0; i < jobId.length; i += 1) {
    n ^= jobId.charCodeAt(i);
    n = Math.imul(n, 16777619);
  }
  let token = '';
  for (let i = 0; i < 11; i += 1) {
    token += alphabet[Math.abs(n) % 62];
    n = Math.imul(n, 1664525) + 1013904223;
  }
  return `https://scoutlytics.gg/r/${token}`;
}

const LIFECYCLE: HighlightJobStatus[] = [
  'downloading',
  'source_ready',
  'detecting',
  'detect_ready',
  'rendering',
  'delivered',
];

// expires_at is mock-only so the countdown UI is testable. Production CRA
// bakes REACT_APP_RTTS_USE_MOCK=false (workflow + deploy-production.sh), so
// this client cannot mount on a production build.
const MOCK_VODS: Vod[] = [
  {
    vod_id: 'vod_8f3a21',
    title: 'Ranked grind — clutch overtime finishes',
    duration_sec: 72 * 60 + 14,
    created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    thumbnail_url: 'https://static-cdn.jtvnw.net/ttv-static/404_preview-320x180.jpg',
    expires_at: new Date(Date.now() + 13 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    vod_id: 'vod_b91c04',
    title: 'Community night: viewer games + highlights',
    duration_sec: 45 * 60 + 33,
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    thumbnail_url: 'https://static-cdn.jtvnw.net/ttv-static/404_preview-320x180.jpg',
  },
  {
    vod_id: 'vod_short25',
    title: 'Quick clip — 25s test (too short to reel)',
    duration_sec: 25,
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    thumbnail_url: 'https://static-cdn.jtvnw.net/ttv-static/404_preview-320x180.jpg',
  },
  {
    vod_id: 'vod_d2e778',
    title: 'Tournament finals VODs — full series',
    duration_sec: 88 * 60 + 5,
    created_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    thumbnail_url: 'https://static-cdn.jtvnw.net/ttv-static/404_preview-320x180.jpg',
    expires_at: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    vod_id: 'vod_short5m',
    title: 'Warmup lobby — five minutes',
    duration_sec: 5 * 60,
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    thumbnail_url: 'https://static-cdn.jtvnw.net/ttv-static/404_preview-320x180.jpg',
  },
  {
    vod_id: 'vod_11aa90',
    title: 'Late night chill — just chatting + clips',
    duration_sec: 28 * 60 + 47,
    created_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    thumbnail_url: 'https://static-cdn.jtvnw.net/ttv-static/404_preview-320x180.jpg',
  },
  {
    vod_id: 'vod_55cf12',
    title: 'Scrims block 3 — midlane masterclass',
    duration_sec: 61 * 60 + 2,
    created_at: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    thumbnail_url: 'https://static-cdn.jtvnw.net/ttv-static/404_preview-320x180.jpg',
    expires_at: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    rtts_game_guess: 'unknown',
    rtts_vod_eligible: true,
  },
  {
    vod_id: 'vod_fortnite',
    title: 'Fortnite ranked — Chapter 5',
    duration_sec: 74 * 60,
    created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    thumbnail_url: 'https://static-cdn.jtvnw.net/ttv-static/404_preview-320x180.jpg',
    rtts_game_guess: 'other',
    rtts_vod_eligible: false,
  },
];

type MockJobRecord = {
  job: HighlightJob;
  stageIndex: number;
  createdAt: number;
  failAtEnd: boolean;
  twitchVodId?: string;
  /** Once curation is submitted (or timed out), do not pause again. */
  curationResolved?: boolean;
};

const jobStore = new Map<string, MockJobRecord>();
/** Soft-deleted job ids for mock library. */
const deletedReelIds = new Set<string>();
/** Dedicated library seed rows (separate from acquire jobStore lifecycle). */
let librarySeed: ReelSummary[] | null = null;
/** Poll ticks for advancing the seeded in-progress library reel. */
let libraryProgressTicks = 0;
/**
 * When true, each subsequent getReels advances the seeded cutting reel.
 * Off by default in tests so badge + page mounts don't instantly finish it.
 */
let libraryAutoAdvance = process.env.NODE_ENV !== 'test';
/** When true, mock jobs pause at awaiting_curation instead of auto-rendering. */
let mockCurationPauseEnabled = false;

/** Seeded existing reel for already_exists scenario. */
const EXISTING_JOB_ID = 'job_existing_reel';

function assertLinked(): void {
  const state = getMockAuthState();
  if (state === 'logged_out' || state === 'stale_link') {
    throw new TwitchSignInRequiredError('sign in with Twitch');
  }
}

/** Session present (profile/portal) — Twitch link not required. */
function assertSession(): void {
  if (getMockAuthState() === 'logged_out') {
    throw new TwitchSignInRequiredError('sign in with Twitch');
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function buildMockProfile(): Profile {
  const fields = getMockProfileFields();
  return {
    twitch_handle: 'mock_creator',
    avatar_url: 'https://static-cdn.jtvnw.net/jtv_user_pictures/xarth/404_user_70x70.png',
    display_name: fields.display_name,
    email: fields.email,
    onboarded: getMockOnboarded(),
  };
}

function resetsAtIso(daysFromNow = 12): string {
  return new Date(Date.now() + daysFromNow * 24 * 60 * 60 * 1000).toISOString();
}

function deliveredFields(
  jobId: string,
  fresh = true
): Pick<
  HighlightJob,
  'signed_url' | 'expires_at' | 'reel_duration_sec' | 'poster_url' | 'share_url'
> {
  const expiresMs = fresh ? 15 * 60 * 1000 : -60 * 1000;
  return {
    signed_url: `${MOCK_SAMPLE_MP4}?t=${Date.now()}`,
    expires_at: new Date(Date.now() + expiresMs).toISOString(),
    reel_duration_sec: 58,
    poster_url: MOCK_VODS[0]?.thumbnail_url ?? null,
    share_url: mockPublicShareUrl(jobId),
  };
}

function ensureExistingJob(): MockJobRecord {
  const existing = jobStore.get(EXISTING_JOB_ID);
  if (existing) return existing;
  const record: MockJobRecord = {
    stageIndex: LIFECYCLE.length - 1,
    createdAt: Date.now() - 60_000,
    failAtEnd: false,
    twitchVodId: 'vod_already',
    job: {
      id: EXISTING_JOB_ID,
      status: 'delivered',
      ...deliveredFields(EXISTING_JOB_ID, true),
      reel_name: 'Week 4 comeback',
      error_code: null,
    },
  };
  jobStore.set(EXISTING_JOB_ID, record);
  return record;
}

function createJob(
  id: string,
  options: {
    failAtEnd?: boolean;
    twitchVodId?: string;
    startDelivered?: boolean;
    reelName?: string | null;
    durationTier?: string;
  } = {}
): MockJobRecord {
  const failAtEnd = Boolean(options.failAtEnd);
  const startDelivered = Boolean(options.startDelivered);
  const stageIndex = startDelivered ? LIFECYCLE.length - 1 : 0;
  const status = failAtEnd && startDelivered ? 'failed' : LIFECYCLE[stageIndex];
  const record: MockJobRecord = {
    stageIndex,
    createdAt: Date.now(),
    failAtEnd,
    twitchVodId: options.twitchVodId,
    job: {
      id,
      status,
      signed_url: startDelivered && !failAtEnd ? deliveredFields(id).signed_url : null,
      expires_at: startDelivered && !failAtEnd ? deliveredFields(id).expires_at : null,
      reel_duration_sec:
        startDelivered && !failAtEnd ? deliveredFields(id).reel_duration_sec : null,
      poster_url: startDelivered && !failAtEnd ? deliveredFields(id).poster_url : null,
      share_url: startDelivered && !failAtEnd ? deliveredFields(id).share_url : null,
      reel_name:
        options.reelName && isReelNameValid(options.reelName)
          ? normalizeReelName(options.reelName)
          : null,
      error_code: failAtEnd && startDelivered ? 'render_failed' : null,
      reel_duration_tier: options.durationTier || 'mid',
    },
  };
  jobStore.set(id, record);
  return record;
}

function advanceJob(record: MockJobRecord): HighlightJob {
  const stageMs = mockJobStageMs();
  if (stageMs === 0) {
    // Tests: jump toward terminal quickly on each poll.
    if (record.failAtEnd) {
      record.job = {
        ...record.job,
        status: 'failed',
        error_code: 'render_failed',
        signed_url: null,
        expires_at: null,
      };
      return { ...record.job };
    }
    if (record.job.status !== 'delivered') {
      record.stageIndex = LIFECYCLE.length - 1;
      record.job = {
        ...record.job,
        status: 'delivered',
        error_code: null,
        ...deliveredFields(record.job.id, true),
      };
    } else {
      // Re-fetch after expiry: always mint a fresh signed URL.
      record.job = {
        ...record.job,
        ...deliveredFields(record.job.id, true),
      };
    }
    return { ...record.job };
  }

  if (record.job.status === 'delivered') {
    // Refresh signed URL on re-fetch (MVP expiry path).
    record.job = { ...record.job, ...deliveredFields(record.job.id, true) };
    return { ...record.job };
  }
  if (record.job.status === 'failed') {
    return { ...record.job };
  }

  const elapsed = Date.now() - record.createdAt;
  const targetIndex = Math.min(
    LIFECYCLE.length - 1,
    Math.floor(elapsed / stageMs)
  );

  if (record.failAtEnd && targetIndex >= LIFECYCLE.length - 1) {
    record.job = {
      ...record.job,
      status: 'failed',
      error_code: 'render_failed',
      signed_url: null,
      expires_at: null,
    };
    return { ...record.job };
  }

  record.stageIndex = targetIndex;
  const status = LIFECYCLE[targetIndex];
  if (status === 'delivered') {
    record.job = {
      ...record.job,
      status: 'delivered',
      error_code: null,
      ...deliveredFields(record.job.id, true),
    };
  } else {
    record.job = {
      ...record.job,
      status,
      signed_url: null,
      expires_at: null,
      error_code: null,
    };
  }
  return { ...record.job };
}

/** Mock: GET /api/rtts/twitch/vods */
export async function mockGetVods(): Promise<Vod[]> {
  await mockLatency();
  assertLinked();
  const scenario = getMockScenario();
  if (scenario === 'vods_empty') {
    return [];
  }
  if (scenario === 'vods_unsupported_game') {
    return MOCK_VODS.filter((vod) => vod.rtts_vod_eligible === false).map((vod) => ({
      ...vod,
    }));
  }
  return MOCK_VODS.map((vod) => ({ ...vod }));
}

/** Mock: GET /api/rtts/me/entitlement */
export async function mockGetEntitlement(): Promise<Entitlement> {
  await mockLatency();
  assertLinked();

  // After mock checkout, simulate async webhook: stay free for N polls, then paid
  // (or pack credits — pack never flips tier).
  if (getLastCheckoutPlan() !== null) {
    if (consumePostCheckoutFreePoll()) {
      return {
        tier: 'free',
        reels_used: 1,
        free_cap: STUDIO_PRICING.free.reels,
        reels_remaining: 0,
        reel_credits: 0,
        recut_enabled: true,
        recap_only: true,
        trim_enabled: true,
        duration_tiers_enabled: true,
        watermark_opt_in_enabled: true,
        recut_reuse_source: true,
        render_from_cache_enabled: true,
      };
    }
    if (getLastCheckoutPlan() === 'pack5') {
      return {
        tier: 'free',
        reels_used: 1,
        free_cap: STUDIO_PRICING.free.reels,
        reels_remaining: 0,
        reel_credits: STUDIO_PRICING.pack5.reels,
        recut_enabled: true,
        recap_only: true,
        trim_enabled: true,
        duration_tiers_enabled: true,
        watermark_opt_in_enabled: true,
        recut_reuse_source: true,
        render_from_cache_enabled: true,
        can_manage_subscription: false,
      };
    }
    return {
      tier: 'paid',
      reels_used: 1,
      free_cap: STUDIO_PRICING.free.reels,
      reels_remaining: 0,
      reels_used_period: 0,
      period_cap: STUDIO_PRICING.paidCap,
      period_reels_remaining: STUDIO_PRICING.paidCap,
      period_resets_at: resetsAtIso(30),
      recut_enabled: true,
      recap_only: true,
      trim_enabled: true,
      reel_credits: 0,
      watermark_opt_in_enabled: true,
    };
  }

  const scenario = getMockScenario();

  switch (scenario) {
    case 'free_capped':
    case 'checkout_pending':
      return {
        tier: 'free',
        reels_used: 1,
        free_cap: STUDIO_PRICING.free.reels,
        reels_remaining: 0,
        recut_enabled: true,
        recap_only: true,
        trim_enabled: true,
        duration_tiers_enabled: true,
        watermark_opt_in_enabled: true,
        recut_reuse_source: true,
        render_from_cache_enabled: true,
        can_manage_subscription: false,
      };
    case 'paid_ok':
      return {
        tier: 'paid',
        reels_used: 1,
        free_cap: STUDIO_PRICING.free.reels,
        reels_remaining: 0,
        reels_used_period: 3,
        period_cap: STUDIO_PRICING.paidCap,
        period_reels_remaining: STUDIO_PRICING.paidCap - 3,
        period_resets_at: resetsAtIso(),
        recut_enabled: true,
        recap_only: true,
        trim_enabled: true,
        duration_tiers_enabled: true,
        watermark_opt_in_enabled: true,
        recut_reuse_source: true,
        render_from_cache_enabled: true,
        can_manage_subscription: true,
      };
    case 'paid_capped':
      return {
        tier: 'paid',
        reels_used: 1,
        free_cap: STUDIO_PRICING.free.reels,
        reels_remaining: 0,
        reels_used_period: STUDIO_PRICING.paidCap,
        period_cap: STUDIO_PRICING.paidCap,
        period_reels_remaining: 0,
        period_resets_at: resetsAtIso(),
        recut_enabled: true,
        recap_only: true,
        trim_enabled: true,
        duration_tiers_enabled: true,
        watermark_opt_in_enabled: true,
        recut_reuse_source: true,
        render_from_cache_enabled: true,
        can_manage_subscription: true,
      };
    case 'admin_ok':
      return {
        tier: 'admin',
        unlimited: true,
        reels_used: 12,
        free_cap: STUDIO_PRICING.free.reels,
        reels_remaining: null,
        recut_enabled: true,
        recap_only: true,
        trim_enabled: true,
        duration_tiers_enabled: true,
        watermark_opt_in_enabled: true,
        recut_reuse_source: true,
        render_from_cache_enabled: true,
        can_manage_subscription: false,
      };
    case 'already_exists':
      return {
        tier: 'paid',
        reels_used: 1,
        free_cap: STUDIO_PRICING.free.reels,
        reels_remaining: 0,
        reels_used_period: 1,
        period_cap: STUDIO_PRICING.paidCap,
        period_reels_remaining: STUDIO_PRICING.paidCap - 1,
        period_resets_at: resetsAtIso(),
        recut_enabled: true,
        recap_only: true,
        trim_enabled: true,
        duration_tiers_enabled: true,
        watermark_opt_in_enabled: true,
        recut_reuse_source: true,
        render_from_cache_enabled: true,
        can_manage_subscription: true,
      };
    case 'fail':
    case 'free_ok':
    default:
      return {
        tier: 'free',
        reels_used: 0,
        free_cap: STUDIO_PRICING.free.reels,
        reels_remaining: STUDIO_PRICING.free.reels,
        recut_enabled: true,
        recap_only: true,
        trim_enabled: true,
        duration_tiers_enabled: true,
        watermark_opt_in_enabled: true,
        recut_reuse_source: true,
        render_from_cache_enabled: true,
        can_manage_subscription: false,
      };
  }
}

/**
 * Mock: POST /api/rtts/highlights/acquire
 * Body key is `twitch_vod_id` (not `vod_id`).
 */
export async function mockAcquire(
  twitchVodId: string,
  options?: {
    recut?: boolean;
    reelName?: string;
    force?: boolean;
    durationTier?: string;
  }
): Promise<AcquireSuccess> {
  await mockLatency();
  assertLinked();

  const submitted =
    options && 'reelName' in options ? options.reelName : undefined;
  let resolvedName = '';
  if (submitted !== undefined) {
    if (!isReelNameValid(submitted)) {
      throw new AcquireError('reel_name_required', {
        status: 400,
        message: STUDIO_COPY.reelNameRequired,
      });
    }
    resolvedName = normalizeReelName(submitted);
  } else if (options?.recut) {
    const existing = ensureExistingJob();
    if (!isReelNameValid(existing.job.reel_name)) {
      throw new AcquireError('reel_name_required', {
        status: 400,
        message: STUDIO_COPY.reelNameRequired,
      });
    }
    resolvedName = normalizeReelName(existing.job.reel_name);
  } else {
    throw new AcquireError('reel_name_required', {
      status: 400,
      message: STUDIO_COPY.reelNameRequired,
    });
  }

  const targetVod = MOCK_VODS.find((vod) => vod.vod_id === twitchVodId);
  if (targetVod && targetVod.rtts_vod_eligible === false) {
    throw new AcquireError('vod_unsupported_game', {
      status: 400,
      message: STUDIO_COPY.vodUnsupportedGame,
    });
  }

  const scenario = getMockScenario();

  if (scenario === 'free_capped' || scenario === 'checkout_pending') {
    throw new AcquireError('upgrade_required', {
      status: 402,
      message: 'upgrade_required',
    });
  }

  if (scenario === 'paid_capped') {
    // TODO(seam): confirm distinct 402 monthly_cap_reached code with backend
    throw new AcquireError('monthly_cap_reached', {
      status: 402,
      message: 'monthly_cap_reached',
      period_resets_at: resetsAtIso(),
      period_cap: STUDIO_PRICING.paidCap,
    });
  }

  if (scenario === 'already_exists' && !options?.recut) {
    // TODO(seam): confirm 409 body includes existing job_id
    ensureExistingJob();
    throw new AcquireError('reel_already_exists', {
      status: 409,
      message: 'reel_already_exists',
      job_id: EXISTING_JOB_ID,
    });
  }

  // TODO(seam): confirm standalone acquire is unwrapped {job} vs LSC {success,job}
  const id = `job_mock_${twitchVodId}_${Date.now()}`;
  const record = createJob(id, {
    failAtEnd: scenario === 'fail',
    twitchVodId,
    reelName: resolvedName,
    durationTier: options?.durationTier,
  });

  return { job: { ...record.job } };
}

/** Mock: GET /api/rtts/highlights/jobs/{id} */
export async function mockGetJob(id: string): Promise<{ job: HighlightJob }> {
  await mockLatency();
  assertLinked();

  // Library-managed jobs: status is driven by getReels polls, not stage timer.
  if (id.startsWith('job_lib_')) {
    ensureLibrarySeed();
    syncLibraryCuttingIntoJobStore();
    const libRecord = jobStore.get(id);
    if (libRecord && !deletedReelIds.has(id)) {
      return { job: { ...libRecord.job } };
    }
    throw new Error('job_not_found');
  }

  let record = jobStore.get(id);
  if (!record && id === EXISTING_JOB_ID) {
    record = ensureExistingJob();
  }
  if (!record) {
    // Unknown id: treat as expired / not retrievable.
    throw new Error('job_not_found');
  }

  if (
    mockCurationPauseEnabled &&
    !record.curationResolved &&
    record.job.status === 'awaiting_curation'
  ) {
    return { job: { ...record.job } };
  }

  // Test fast-path (stageMs=0) jumps straight to delivered — pause before that.
  if (
    mockCurationPauseEnabled &&
    !record.curationResolved &&
    !record.failAtEnd &&
    mockJobStageMs() === 0 &&
    record.job.status !== 'delivered' &&
    record.job.status !== 'failed'
  ) {
    record.job = {
      ...record.job,
      status: 'awaiting_curation',
      curation_deadline_at: new Date(Date.now() + 30 * 60_000).toISOString(),
    };
    return { job: { ...record.job } };
  }

  const job = advanceJob(record);
  if (
    mockCurationPauseEnabled &&
    !record.curationResolved &&
    !record.failAtEnd &&
    (job.status === 'detect_ready' || job.status === 'rendering')
  ) {
    record.job = {
      ...record.job,
      status: 'awaiting_curation',
      curation_deadline_at: new Date(Date.now() + 30 * 60_000).toISOString(),
    };
    return { job: { ...record.job } };
  }
  return { job };
}

/** Mock: GET /api/rtts/highlights/jobs/{id}/candidates */
export async function mockGetCandidates(
  jobId: string
): Promise<import('./types').CandidatesSuccess> {
  await mockLatency();
  assertLinked();
  const record = jobStore.get(jobId);
  if (!record) throw new Error('job_not_found');
  return {
    candidates: [
      {
        candidate_id: '0',
        start_sec: 10,
        end_sec: 16,
        duration_sec: 6,
        current_start_sec: 10,
        current_end_sec: 16,
        thumbnail_url: null,
        label: 'Bears TD pass',
        why: 'Score',
        team: 'Bears',
        spectacle_score: 1.2,
        selected_by_default: true,
        in_current_reel: true,
        in_reel: true,
      },
      {
        candidate_id: '1',
        start_sec: 40,
        end_sec: 44,
        duration_sec: 4,
        current_start_sec: 40,
        current_end_sec: 44,
        thumbnail_url: null,
        label: 'Ravens sack',
        why: 'Pressure',
        team: 'Ravens',
        spectacle_score: 1.0,
        selected_by_default: false,
        in_current_reel: false,
        in_reel: false,
      },
      {
        candidate_id: '2',
        start_sec: 70,
        end_sec: 78,
        duration_sec: 8,
        current_start_sec: 70,
        current_end_sec: 78,
        thumbnail_url: null,
        label: 'Chicago Bears INT',
        why: 'Takeaway',
        team: 'Bears',
        spectacle_score: 1.1,
        selected_by_default: true,
        in_current_reel: true,
        in_reel: true,
      },
    ],
    curation: {
      status: record.job.status,
      deadline_at: record.job.curation_deadline_at ?? null,
      submitted_at: null,
      auto_submitted: false,
      selected: [],
      curate_enabled: true,
      curate_max_clips: 20,
      curate_max_duration_sec: 180,
    },
    // Public sample MP4 so seek-and-stop is testable without Part A live.
    source_preview_url: `${MOCK_SAMPLE_MP4}?curation=1`,
    source_preview_expires_at: new Date(Date.now() + 20 * 60_000).toISOString(),
    trim_enabled: true,
    curate_enabled: true,
  };
}

/** Mock: POST /api/rtts/highlights/jobs/{id}/curate */
export async function mockSubmitCurate(
  jobId: string,
  selected: string[],
  reelName?: string
): Promise<{ job: HighlightJob }> {
  await mockLatency();
  assertLinked();
  const record = jobStore.get(jobId);
  if (!record) throw new Error('job_not_found');
  if (!Array.isArray(selected) || selected.length === 0) {
    throw new Error('curate failed: 400');
  }
  if (reelName !== undefined && !isReelNameValid(reelName)) {
    throw new AcquireError('reel_name_required', {
      status: 400,
      message: STUDIO_COPY.reelNameRequired,
    });
  }
  if (reelName === undefined && !isReelNameValid(record.job.reel_name)) {
    throw new AcquireError('reel_name_required', {
      status: 400,
      message: STUDIO_COPY.reelNameRequired,
    });
  }
  record.curationResolved = true;
  record.job = {
    ...record.job,
    status: 'rendering',
    curation_deadline_at: null,
    reel_name: reelName
      ? normalizeReelName(reelName)
      : normalizeReelName(record.job.reel_name),
  };
  // Resume toward delivered on subsequent polls.
  record.stageIndex = Math.max(record.stageIndex, LIFECYCLE.indexOf('rendering'));
  record.createdAt = Date.now() - mockJobStageMs() * record.stageIndex;
  return { job: { ...record.job } };
}

/** Mock: POST /api/rtts/highlights/jobs/{id}/refine — free re-render, no dock. */
export async function mockSubmitRefine(
  jobId: string,
  clips: import('./types').RefineClip[],
  music?: import('./types').RefineMusicOptions | null
): Promise<{ job: HighlightJob; docked_reel: boolean }> {
  await mockLatency();
  assertLinked();
  const record = jobStore.get(jobId);
  if (!record) throw new Error('job_not_found');
  if (!Array.isArray(clips) || clips.length === 0) {
    throw new Error('refine failed: 400');
  }
  const musicPatch: Partial<HighlightJob> = {};
  if (music !== undefined && music !== null) {
    musicPatch.music_id = music.music_id;
    musicPatch.music_mode = music.music_mode;
    musicPatch.game_gain_db = music.game_gain_db;
    musicPatch.music_gain_db = music.music_gain_db;
    musicPatch.music_loop = music.music_loop;
    musicPatch.music_fade_in_s = music.music_fade_in_s;
    musicPatch.music_fade_out_s = music.music_fade_out_s;
  } else if (music === null) {
    musicPatch.music_id = null;
    musicPatch.music_mode = null;
    musicPatch.game_gain_db = null;
    musicPatch.music_gain_db = null;
    musicPatch.music_loop = null;
    musicPatch.music_fade_in_s = null;
    musicPatch.music_fade_out_s = null;
  }
  record.job = {
    ...record.job,
    ...musicPatch,
    status: 'rendering',
    signed_url: null,
    expires_at: null,
  };
  record.stageIndex = Math.max(record.stageIndex, LIFECYCLE.indexOf('rendering'));
  record.createdAt = Date.now() - mockJobStageMs() * record.stageIndex;
  return { job: { ...record.job }, docked_reel: false };
}

/** Mock: POST /api/rtts/highlights/jobs/{id}/rerender-duration */
export async function mockSubmitDurationRerender(
  jobId: string,
  durationTier: string
): Promise<{ job: HighlightJob; docked_reel: boolean }> {
  await mockLatency();
  assertLinked();
  const record = jobStore.get(jobId);
  if (!record) throw new Error('job_not_found');
  record.job = {
    ...record.job,
    reel_duration_tier: durationTier,
    status: 'rendering',
    signed_url: null,
    expires_at: null,
    detect_reused: true,
  };
  record.stageIndex = Math.max(record.stageIndex, LIFECYCLE.indexOf('rendering'));
  record.createdAt = Date.now() - mockJobStageMs() * record.stageIndex;
  return { job: { ...record.job }, docked_reel: false };
}

/** Mock: POST /api/rtts/highlights/jobs/{id}/accept — dock + clean re-render. */
export async function mockSubmitAccept(
  jobId: string,
  music?: import('./types').RefineMusicOptions | null
): Promise<{
  job: HighlightJob;
  docked_reel: boolean;
  already_accepted: boolean;
}> {
  await mockLatency();
  assertLinked();
  const record = jobStore.get(jobId);
  if (!record) throw new Error('job_not_found');
  if (record.job.accepted) {
    return {
      job: { ...record.job },
      docked_reel: false,
      already_accepted: true,
    };
  }
  const scenario = getMockScenario();
  if (scenario === 'free_capped') {
    throw new AcquireError('upgrade_required', { status: 402, message: 'upgrade_required' });
  }
  if (scenario === 'paid_capped') {
    throw new AcquireError('monthly_cap_reached', {
      status: 402,
      message: 'monthly_cap_reached',
      period_resets_at: resetsAtIso(),
      period_cap: STUDIO_PRICING.paidCap,
    });
  }
  const musicPatch: Partial<HighlightJob> = {};
  if (music !== undefined && music !== null) {
    musicPatch.music_id = music.music_id;
    musicPatch.music_mode = music.music_mode;
    musicPatch.game_gain_db = music.game_gain_db;
    musicPatch.music_gain_db = music.music_gain_db;
    musicPatch.music_loop = music.music_loop;
    musicPatch.music_fade_in_s = music.music_fade_in_s;
    musicPatch.music_fade_out_s = music.music_fade_out_s;
  } else if (music === null) {
    musicPatch.music_id = null;
    musicPatch.music_mode = null;
    musicPatch.game_gain_db = null;
    musicPatch.music_gain_db = null;
    musicPatch.music_loop = null;
    musicPatch.music_fade_in_s = null;
    musicPatch.music_fade_out_s = null;
  }
  record.job = {
    ...record.job,
    ...musicPatch,
    accepted: true,
    accepted_at: new Date().toISOString(),
    watermark: false,
    signoff_preview: false,
    status: 'rendering',
    signed_url: null,
    expires_at: null,
  };
  record.stageIndex = Math.max(record.stageIndex, LIFECYCLE.indexOf('rendering'));
  record.createdAt = Date.now() - mockJobStageMs() * record.stageIndex;
  return {
    job: { ...record.job },
    docked_reel: true,
    already_accepted: false,
  };
}

/** Mock: GET /api/rtts/music/manifest — bundled catalog. */
export async function mockGetMusicManifest(): Promise<{
  tracks: import('../music/types').MusicTrack[];
}> {
  await mockLatency();
  const { loadBundledTracks } = await import('../music/loadTracks');
  return { tracks: loadBundledTracks() };
}

export function setMockCurationPauseEnabled(enabled: boolean): void {
  mockCurationPauseEnabled = enabled;
}

/**
 * Stubbed Twitch connect for mock mode.
 * Flips mock auth to `linked`. Swap to real OAuth via beginTwitchConnect seam.
 */
export function mockBeginTwitchConnect(): void {
  setMockAuthState('linked');
}

/**
 * Mock: POST /api/rtts/billing/checkout
 * Records plan and starts async entitlement flip. Returns a fake checkout_url
 * pointing at the frontend success route (mock CTA navigates client-side).
 */
export async function mockCheckout(plan: StudioCheckoutPlan): Promise<CheckoutSuccess> {
  await mockLatency();
  assertLinked();
  setLastCheckoutPlan(plan);
  beginMockCheckoutActivation({
    pendingLong: getMockScenario() === 'checkout_pending',
  });
  // Fake Stripe URL — mock UI navigates to success instead of assigning.
  return {
    checkout_url: `https://checkout.stripe.test/mock?plan=${plan}&success=${encodeURIComponent(
      STUDIO_CHECKOUT_ROUTES.success
    )}`,
  };
}

/** Mock: POST /api/rtts/me/highlight-interest */
export async function mockRecordHighlightInterest(): Promise<{
  recorded: boolean;
  already: boolean;
}> {
  await mockLatency();
  assertSession();
  const key = 'rtts_studio_mock_highlight_interest';
  const already = sessionStorage.getItem(key) === '1';
  sessionStorage.setItem(key, '1');
  return { recorded: true, already };
}

/** Mock: GET /api/rtts/me/feedback */
export async function mockGetFeedbackChannel(): Promise<{
  mailto: string;
  channel: string;
}> {
  await mockLatency(10, 20);
  assertSession();
  return {
    mailto: 'mailto:antoine@scoutlytics.gg?subject=RTTS%20Friends%20Beta%20Feedback',
    channel: 'in_product',
  };
}

/** Mock: POST /api/rtts/me/feedback */
export async function mockSubmitFeedback(
  message: string,
  _category = 'general'
): Promise<{ recorded: boolean; feedback_id?: string }> {
  await mockLatency();
  assertSession();
  if (!String(message || '').trim()) {
    throw new Error('empty_message');
  }
  return { recorded: true, feedback_id: 'fb-mock-1' };
}

/** Mock: GET /api/rtts/me/profile — session only. */
export async function mockGetProfile(): Promise<Profile> {
  await mockLatency();
  assertSession();
  return buildMockProfile();
}

/** Mock: PUT /api/rtts/me/profile */
export async function mockUpdateProfile(patch: ProfileUpdate): Promise<Profile> {
  await mockLatency();
  assertSession();

  if (patch.email !== undefined && patch.email !== null && String(patch.email).trim()) {
    const cleaned = String(patch.email).trim();
    if (!EMAIL_RE.test(cleaned)) {
      throw new InvalidEmailError('Enter a valid email');
    }
    setMockProfileFields({ email: cleaned });
  } else if (patch.email === null || patch.email === '') {
    setMockProfileFields({ email: null });
  }

  if (patch.display_name !== undefined) {
    const name =
      patch.display_name === null ? null : String(patch.display_name).trim() || null;
    setMockProfileFields({ display_name: name });
  }

  if (patch.onboarded === true) {
    setMockOnboarded(true);
  }

  return buildMockProfile();
}

/**
 * Mock: POST /api/rtts/billing/portal
 * Returns a fake portal URL; UI navigates to return route in mock mode.
 */
export async function mockCreatePortalSession(): Promise<PortalSuccess> {
  await mockLatency();
  assertSession();
  const scenario = getMockScenario();
  if (scenario === 'free_ok' || scenario === 'free_capped' || scenario === 'fail') {
    // Free / no customer → same as backend 409.
    throw new PortalError('no_active_subscription', {
      status: 409,
      message: 'no_active_subscription',
    });
  }
  return {
    portal_url: `https://billing.stripe.test/mock-portal?return=${encodeURIComponent(
      STUDIO_PORTAL_RETURN_ROUTE
    )}`,
  };
}

function isoAgo(hours: number): string {
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

function ensureLibrarySeed(): ReelSummary[] {
  if (librarySeed) return librarySeed;
  const thumb = MOCK_VODS[0]?.thumbnail_url ?? null;
  librarySeed = [
    {
      job_id: 'job_lib_delivered_1',
      provider_vod_id: 'vod_8f3a21',
      vod_title: 'Ranked grind — clutch overtime finishes',
      reel_name: 'Ranked grind — clutch overtime finishes',
      status: 'delivered',
      created_at: isoAgo(5),
      updated_at: isoAgo(4),
      signed_url: `${MOCK_SAMPLE_MP4}?lib=1`,
      share_url: mockPublicShareUrl('job_lib_delivered_1'),
      expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
      poster_url: thumb,
      reel_duration_sec: 62,
      error_code: null,
    },
    {
      job_id: 'job_lib_delivered_2',
      provider_vod_id: 'vod_b91c04',
      vod_title: null,
      reel_name: null,
      needs_reel_name: true,
      status: 'delivered',
      created_at: isoAgo(28),
      updated_at: isoAgo(27),
      signed_url: `${MOCK_SAMPLE_MP4}?lib=2`,
      share_url: mockPublicShareUrl('job_lib_delivered_2'),
      expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
      poster_url: null,
      reel_duration_sec: 44,
      error_code: null,
    },
    {
      job_id: 'job_lib_cutting',
      provider_vod_id: 'vod_d2e778',
      vod_title: 'Tournament finals VODs — full series',
      reel_name: 'Tournament finals',
      status: 'rendering',
      created_at: isoAgo(0.2),
      updated_at: isoAgo(0.1),
      signed_url: null,
      expires_at: null,
      poster_url: null,
      reel_duration_sec: null,
      error_code: null,
    },
    {
      job_id: 'job_lib_failed',
      provider_vod_id: 'vod_11aa90',
      vod_title: 'Late night chill — just chatting + clips',
      reel_name: 'Late night chill',
      status: 'failed',
      created_at: isoAgo(50),
      updated_at: isoAgo(49),
      signed_url: null,
      expires_at: null,
      poster_url: null,
      reel_duration_sec: null,
      error_code: 'render_failed',
    },
  ];
  libraryProgressTicks = 0;
  // Mirror seed into jobStore so /studio/reel/:id can poll getJob.
  for (const row of librarySeed) {
    if (jobStore.has(row.job_id) || deletedReelIds.has(row.job_id)) continue;
    const delivered = row.status === 'delivered';
    const failed = row.status === 'failed';
    jobStore.set(row.job_id, {
      stageIndex: delivered
        ? LIFECYCLE.length - 1
        : Math.max(0, LIFECYCLE.indexOf(row.status as HighlightJobStatus)),
      createdAt: Date.parse(row.created_at) || Date.now(),
      failAtEnd: failed,
      twitchVodId: row.provider_vod_id,
      curationResolved: true,
      job: {
        id: row.job_id,
        status: row.status,
        signed_url: row.signed_url ?? null,
        expires_at: row.expires_at ?? null,
        poster_url: row.poster_url ?? null,
        reel_duration_sec: row.reel_duration_sec ?? null,
        error_code: row.error_code ?? null,
      },
    });
  }
  return librarySeed;
}

function syncLibraryCuttingIntoJobStore(): void {
  if (!librarySeed) return;
  const cutting = librarySeed.find((r) => r.job_id === 'job_lib_cutting');
  if (!cutting) return;
  const record = jobStore.get('job_lib_cutting');
  if (!record) return;
  record.job = {
    ...record.job,
    status: cutting.status,
    signed_url: cutting.signed_url ?? null,
    expires_at: cutting.expires_at ?? null,
    poster_url: cutting.poster_url ?? null,
    reel_duration_sec: cutting.reel_duration_sec ?? null,
    error_code: cutting.error_code ?? null,
  };
  if (cutting.status === 'delivered') {
    record.stageIndex = LIFECYCLE.length - 1;
    record.failAtEnd = false;
  }
}

function advanceLibrarySeedInPlace(): void {
  const seed = ensureLibrarySeed();
  const cutting = seed.find((r) => r.job_id === 'job_lib_cutting');
  if (!cutting || cutting.status === 'delivered' || cutting.status === 'failed') {
    return;
  }
  libraryProgressTicks += 1;
  const stageMs = mockJobStageMs();
  // Tests (stageMs=0): advance every poll. Live mock: ~2–3 polls per stage.
  const ticksPerStage = stageMs === 0 ? 1 : 2;
  const stage = Math.floor(libraryProgressTicks / ticksPerStage);
  if (stage <= 0) {
    cutting.status = 'rendering';
  } else if (stage === 1) {
    cutting.status = 'render_ready';
    cutting.updated_at = new Date().toISOString();
  } else {
    cutting.status = 'delivered';
    cutting.updated_at = new Date().toISOString();
    cutting.signed_url = `${MOCK_SAMPLE_MP4}?lib=cutting`;
    cutting.expires_at = new Date(Date.now() + 15 * 60 * 1000).toISOString();
    cutting.poster_url = MOCK_VODS[3]?.thumbnail_url ?? MOCK_VODS[0]?.thumbnail_url ?? null;
    cutting.reel_duration_sec = 71;
    cutting.error_code = null;
  }
}

function jobRecordToReelSummary(record: MockJobRecord): ReelSummary {
  const job = record.job;
  const vod = MOCK_VODS.find((v) => v.vod_id === record.twitchVodId);
  const base: ReelSummary = {
    job_id: job.id,
    provider_vod_id: record.twitchVodId || 'vod_unknown',
    vod_title: vod?.title ?? null,
    reel_name: job.reel_name ?? null,
    needs_reel_name: !isReelNameValid(job.reel_name),
    status: job.status,
    created_at: new Date(record.createdAt).toISOString(),
    updated_at: new Date().toISOString(),
  };
  if (job.status === 'delivered') {
    base.signed_url = job.signed_url ?? null;
    base.expires_at = job.expires_at ?? null;
    base.poster_url = job.poster_url ?? null;
    base.reel_duration_sec = job.reel_duration_sec ?? null;
    base.share_url = job.share_url ?? mockPublicShareUrl(job.id);
  }
  if (job.status === 'failed' && job.error_code) {
    base.error_code = job.error_code;
  }
  return base;
}

function buildMockReelsList(): ReelSummary[] {
  if (getMockScenario() === 'reels_empty') {
    return [];
  }

  const firstSeed = librarySeed === null;
  ensureLibrarySeed();
  // Advance only on subsequent polls so the first response stays mid-cut.
  if (!firstSeed && libraryAutoAdvance) {
    advanceLibrarySeedInPlace();
    syncLibraryCuttingIntoJobStore();
  }
  const byId = new Map<string, ReelSummary>();

  for (const row of ensureLibrarySeed()) {
    if (!deletedReelIds.has(row.job_id)) {
      byId.set(row.job_id, { ...row });
    }
  }

  for (const [id, record] of jobStore.entries()) {
    if (deletedReelIds.has(id)) continue;
    // Library seed rows are the source of truth for job_lib_* (progress via polls).
    if (id.startsWith('job_lib_')) continue;
    // Advance acquire jobs so library mirrors live progress.
    if (record.job.status !== 'delivered' && record.job.status !== 'failed') {
      advanceJob(record);
    }
    byId.set(id, jobRecordToReelSummary(record));
  }

  return Array.from(byId.values()).sort(
    (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)
  );
}

/** Mock: GET /api/rtts/me/reels */
export async function mockGetReels(): Promise<{ reels: ReelSummary[] }> {
  await mockLatency();
  assertSession();
  return { reels: buildMockReelsList() };
}

/** Mock: POST /api/rtts/me/reels/{jobId}/share */
export async function mockEnsureShareLink(
  jobId: string
): Promise<{ share_url: string }> {
  await mockLatency();
  assertSession();
  const id = String(jobId || '').trim();
  if (!id || deletedReelIds.has(id)) {
    throw new Error('ensureShareLink failed: 404');
  }
  const share_url = mockPublicShareUrl(id);
  const record = jobStore.get(id);
  if (record) {
    record.job.share_url = share_url;
  }
  if (librarySeed) {
    librarySeed = librarySeed.map((row) =>
      row.job_id === id ? { ...row, share_url } : row
    );
  }
  return { share_url };
}

/** Mock: DELETE /api/rtts/me/reels/{jobId} */
export async function mockDeleteReel(jobId: string): Promise<void> {
  await mockLatency();
  assertSession();
  const id = String(jobId || '').trim();
  if (!id) throw new Error('deleteReel failed: 404');
  const inSeed = ensureLibrarySeed().some((r) => r.job_id === id);
  const inJobs = jobStore.has(id);
  if (!inSeed && !inJobs) {
    throw new Error('deleteReel failed: 404');
  }
  deletedReelIds.add(id);
  jobStore.delete(id);
  if (librarySeed) {
    librarySeed = librarySeed.filter((r) => r.job_id !== id);
  }
}

/** Mock: PATCH /api/rtts/me/reels/{jobId}/name */
export async function mockRenameReel(
  jobId: string,
  reelName: string
): Promise<ReelSummary> {
  await mockLatency();
  assertSession();
  const id = String(jobId || '').trim();
  if (!id || deletedReelIds.has(id)) {
    throw new Error('renameReel failed: 404');
  }
  if (!isReelNameValid(reelName)) {
    throw new AcquireError('reel_name_required', {
      status: 400,
      message: STUDIO_COPY.reelNameRequired,
    });
  }
  const named = normalizeReelName(reelName);
  const record = jobStore.get(id);
  if (record) {
    record.job.reel_name = named;
  }
  ensureLibrarySeed();
  if (librarySeed) {
    librarySeed = librarySeed.map((row) =>
      row.job_id === id
        ? { ...row, reel_name: named, needs_reel_name: false }
        : row
    );
  }
  const list = await mockGetReels();
  const reel = list.reels.find((row) => row.job_id === id);
  if (!reel) {
    throw new Error('renameReel failed: 404');
  }
  return reel;
}

export type MusicAddonMix = {
  music_volume: number;
  reel_audio_volume: number;
};

/** Mock: POST /api/rtts/me/reels/{jobId}/addons/music */
export async function mockApplyMusicAddon(
  jobId: string,
  musicId: string,
  mix: MusicAddonMix = { music_volume: 45, reel_audio_volume: 100 }
): Promise<{ job: HighlightJob }> {
  await mockLatency();
  assertSession();
  const id = String(jobId || '').trim();
  const record = jobStore.get(id);
  if (!id || deletedReelIds.has(id) || !record || record.job.status !== 'delivered') {
    throw new Error('applyMusicAddon failed: 404');
  }
  const entitlement = await mockGetEntitlement();
  const pro =
    entitlement.unlimited === true ||
    entitlement.tier === 'paid' ||
    entitlement.tier === 'admin' ||
    entitlement.tier === 'comped';
  if (!pro) {
    throw new AcquireError('upgrade_required', {
      status: 402,
      message: 'Music add-ons are included with Pro.',
    });
  }
  record.job = {
    ...record.job,
    music_id: musicId,
    music_source: 'library',
    music_asset_id: null,
    music_asset_name: null,
    music_mode: 'bed',
    music_addon_level: null,
    music_volume: mix.music_volume,
    reel_audio_volume: mix.reel_audio_volume,
    reel_active_version: 'mix',
    music_addon_status: 'ready',
    signed_url: `${MOCK_SAMPLE_MP4}?music=${encodeURIComponent(musicId)}`,
  };
  return { job: { ...record.job } };
}

export type AddonMixPayload = {
  music_id?: string | null;
  music_volume: number;
  reel_audio_volume: number;
  voice_volume?: number;
  clear_music?: boolean;
};

/** Mock: POST /api/rtts/me/reels/{jobId}/addons/mix */
export async function mockApplyAddonMix(
  jobId: string,
  mix: AddonMixPayload
): Promise<{ job: HighlightJob }> {
  await mockLatency();
  assertSession();
  const id = String(jobId || '').trim();
  const record = jobStore.get(id);
  if (!id || deletedReelIds.has(id) || !record || record.job.status !== 'delivered') {
    throw new Error('applyAddonMix failed: 404');
  }
  const entitlement = await mockGetEntitlement();
  const pro =
    entitlement.unlimited === true ||
    entitlement.tier === 'paid' ||
    entitlement.tier === 'admin' ||
    entitlement.tier === 'comped';
  if (!pro) {
    throw new AcquireError('upgrade_required', {
      status: 402,
      message: 'Music add-ons are included with Pro.',
    });
  }
  const clearMusic = mix.clear_music === true;
  const musicId = clearMusic
    ? null
    : mix.music_id || record.job.music_id || null;
  const uploadedMusic =
    !clearMusic &&
    !musicId &&
    (record.job.music_source === 'uploaded' || Boolean(record.job.music_asset_id));
  const hasVoice = Boolean(record.job.voice_asset_id);
  if (!musicId && !uploadedMusic && !hasVoice) {
    if (clearMusic) {
      record.job = {
        ...record.job,
        music_id: null,
        music_source: null,
        music_asset_id: null,
        music_asset_name: null,
        music_mode: null,
        reel_active_version: 'original',
        music_addon_status: 'removed',
      };
      return { job: { ...record.job } };
    }
    throw new Error('applyAddonMix failed: 400');
  }
  record.job = {
    ...record.job,
    music_id: musicId,
    music_source: musicId ? 'library' : uploadedMusic ? 'uploaded' : null,
    music_asset_id: musicId || clearMusic ? null : record.job.music_asset_id,
    music_asset_name: musicId || clearMusic ? null : record.job.music_asset_name,
    music_mode: musicId || uploadedMusic ? 'bed' : record.job.music_mode,
    music_addon_level: null,
    music_volume: mix.music_volume,
    reel_audio_volume: mix.reel_audio_volume,
    voice_volume:
      typeof mix.voice_volume === 'number' ? mix.voice_volume : record.job.voice_volume,
    reel_active_version: 'mix',
    music_addon_status: 'ready',
    signed_url: `${MOCK_SAMPLE_MP4}?mix=${encodeURIComponent(musicId || 'voice')}`,
  };
  return { job: { ...record.job } };
}

/** Mock: POST /api/rtts/me/reels/{jobId}/addons/voice */
export async function mockStoreVoiceAddon(
  jobId: string,
  _file: Blob,
  opts: { voice_source: 'recorded' | 'uploaded'; rights_ack?: boolean }
): Promise<{ job: HighlightJob }> {
  await mockLatency();
  assertSession();
  const id = String(jobId || '').trim();
  const record = jobStore.get(id);
  if (!id || deletedReelIds.has(id) || !record || record.job.status !== 'delivered') {
    throw new Error('storeVoiceAddon failed: 404');
  }
  if (opts.voice_source === 'uploaded' && !opts.rights_ack) {
    throw new Error('storeVoiceAddon failed: 400');
  }
  record.job = {
    ...record.job,
    voice_asset_id: `voice-${id}`,
    voice_source: opts.voice_source,
    voice_volume: record.job.voice_volume ?? 100,
  };
  return { job: { ...record.job } };
}

/** Mock: POST /api/rtts/me/reels/{jobId}/addons/music/upload */
export async function mockStoreMusicAddon(
  jobId: string,
  file: Blob,
  opts: { rights_ack?: boolean } = {}
): Promise<{ job: HighlightJob }> {
  await mockLatency();
  assertSession();
  const id = String(jobId || '').trim();
  const record = jobStore.get(id);
  if (!id || deletedReelIds.has(id) || !record || record.job.status !== 'delivered') {
    throw new Error('storeMusicAddon failed: 404');
  }
  if (!opts.rights_ack) {
    throw new Error('storeMusicAddon failed: 400');
  }
  const name = file instanceof File && file.name ? file.name : 'music-upload';
  record.job = {
    ...record.job,
    music_id: null,
    music_source: 'uploaded',
    music_asset_id: `user-music-${id}`,
    music_asset_name: name,
    music_mode: 'bed',
  };
  return { job: { ...record.job } };
}

/** Mock: DELETE /api/rtts/me/reels/{jobId}/addons/voice */
export async function mockRemoveVoiceAddon(
  jobId: string
): Promise<{ job: HighlightJob }> {
  await mockLatency();
  assertSession();
  const id = String(jobId || '').trim();
  const record = jobStore.get(id);
  if (!id || deletedReelIds.has(id) || !record) {
    throw new Error('removeVoiceAddon failed: 404');
  }
  const hasMusic =
    Boolean(record.job.music_id) ||
    record.job.music_source === 'uploaded' ||
    Boolean(record.job.music_asset_id);
  record.job = {
    ...record.job,
    voice_asset_id: null,
    voice_source: null,
    voice_volume: null,
    reel_active_version: hasMusic ? 'mix' : 'original',
    signed_url: `${MOCK_SAMPLE_MP4}?t=${Date.now()}`,
  };
  return { job: { ...record.job } };
}

/** Mock: DELETE /api/rtts/me/reels/{jobId}/addons/music */
export async function mockRemoveMusicAddon(
  jobId: string
): Promise<{ job: HighlightJob }> {
  await mockLatency();
  assertSession();
  const id = String(jobId || '').trim();
  const record = jobStore.get(id);
  if (!id || deletedReelIds.has(id) || !record) {
    throw new Error('removeMusicAddon failed: 404');
  }
  record.job = {
    ...record.job,
    music_id: null,
    music_source: null,
    music_asset_id: null,
    music_asset_name: null,
    music_mode: null,
    reel_active_version: 'original',
    music_addon_status: 'removed',
    music_addon_level: null,
    music_volume: null,
    reel_audio_volume: null,
    voice_asset_id: null,
    voice_source: null,
    voice_volume: null,
    signed_url: `${MOCK_SAMPLE_MP4}?t=${Date.now()}`,
  };
  return { job: { ...record.job } };
}

/** Mock: POST /api/rtts/me/reels/{jobId}/addons/watermark */
export async function mockApplyWatermarkOptIn(
  jobId: string,
  enabled: boolean
): Promise<{ job: HighlightJob }> {
  await mockLatency();
  assertSession();
  const id = String(jobId || '').trim();
  const record = jobStore.get(id);
  if (!id || deletedReelIds.has(id) || !record || record.job.status !== 'delivered') {
    throw new Error('applyWatermarkOptIn failed: 404');
  }
  const entitlement = await mockGetEntitlement();
  const pro =
    entitlement.unlimited === true ||
    entitlement.tier === 'paid' ||
    entitlement.tier === 'admin' ||
    entitlement.tier === 'comped';
  if (!pro) {
    throw new AcquireError('upgrade_required', {
      status: 403,
      message: 'Watermark branding is included with Pro.',
    });
  }
  record.job = {
    ...record.job,
    watermark_opt_in: enabled === true,
    watermark_status: enabled === true ? 'ready' : null,
    signed_url: `${MOCK_SAMPLE_MP4}?wm=${enabled ? '1' : '0'}&t=${Date.now()}`,
  };
  return { job: { ...record.job } };
}

/** Test helper: clear mock job store. */
export function resetMockJobStore(): void {
  jobStore.clear();
  deletedReelIds.clear();
  librarySeed = null;
  libraryProgressTicks = 0;
  libraryAutoAdvance = process.env.NODE_ENV !== 'test';
  mockCurationPauseEnabled = false;
}

/** Test helper: let getReels advance the seeded in-progress reel. */
export function setMockLibraryAutoAdvance(enabled: boolean): void {
  libraryAutoAdvance = enabled;
}

export function resetMockBillingState(): void {
  resetMockCheckoutState();
}

export function getMockExistingJobId(): string {
  return EXISTING_JOB_ID;
}

export { getLastCheckoutPlan, getPostCheckoutFreePollsRemaining };
